import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
  Button,
} from "../../../components/Component";
import { Badge, Nav, NavItem, NavLink } from "reactstrap";
import { api } from "../../../utils/api";
import { toast } from "react-toastify";
import PermissionGate from "../../../components/permissions/PermissionGate";

const ObservationViewPage = () => {
  const history = useHistory();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [observation, setObservation] = useState(null);
  const [activeTab, setActiveTab] = useState("info");

  useEffect(() => {
    loadObservation();
  }, [id]);

  const loadObservation = async () => {
    try {
      setLoading(true);
      const response = await api.getObservation(id);
      setObservation(response.data || response);
    } catch (error) {
      toast.error("Erreur lors du chargement de l'observation");
      history.push("/observations");
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes == null || Number.isNaN(Number(bytes))) return "—";
    const n = Number(bytes);
    if (n < 1024) return `${n} o`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
    return `${(n / (1024 * 1024)).toFixed(1)} Mo`;
  };

  const openAttachment = async (att) => {
    try {
      const blob = await api.openObservationAttachment(att.id);
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (error) {
      toast.error("Impossible d'ouvrir ce fichier.");
    }
  };

  const downloadAttachment = async (att) => {
    try {
      const blob = await api.downloadObservationAttachment(att.id);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = att.file_name || "piece-jointe";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      toast.error("Impossible de télécharger ce fichier.");
    }
  };

  if (loading) {
    return (
      <React.Fragment>
        <Head title="Détails de l'observation"></Head>
        <Content>
          <Block>
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Chargement...</span>
              </div>
              <p className="mt-3 text-soft">Chargement des détails...</p>
            </div>
          </Block>
        </Content>
      </React.Fragment>
    );
  }

  if (!observation) {
    return (
      <React.Fragment>
        <Head title="Observation introuvable"></Head>
        <Content>
          <Block>
            <div className="text-center py-5">
              <Icon name="info" className="text-soft" style={{ fontSize: '3rem' }}></Icon>
              <p className="mt-3 text-soft">Observation introuvable</p>
              <Button color="primary" onClick={() => history.push("/observations")} className="mt-3">
                Retour à la liste
              </Button>
            </div>
          </Block>
        </Content>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Head title={`Observation: ${observation.title || "Sans titre"}`}></Head>
      <Content>
        <Block size="lg">
          <BlockHead>
            <BlockBetween>
              <BlockHeadContent>
                <BlockTitle page>Détails de l'observation</BlockTitle>
                <div className="d-flex mt-2 gap-2 flex-wrap">
                  {observation.type && (
                    <Badge className="me-1" color={observation.type === 'positive' ? 'success' : observation.type === 'dangerous' ? 'danger' : observation.type === 'near_miss' ? 'warning' : 'info'}>
                      {observation.type === 'positive' ? 'Positive' : observation.type === 'dangerous' ? 'Dangereuse' : observation.type === 'near_miss' ? "Presqu'accident" : 'Non-conformité'}
                    </Badge>
                  )}
                  {observation.severity && (
                    <Badge className="me-1" color={observation.severity === 'low' ? 'success' : observation.severity === 'medium' ? 'warning' : 'danger'}>
                      {observation.severity === 'low' ? 'Faible' : observation.severity === 'medium' ? 'Moyenne' : 'Élevée'}
                    </Badge>
                  )}
                  {observation.status && (
                    <Badge color={observation.status === 'submitted' ? 'secondary' : observation.status === 'received' ? 'info' : observation.status === 'in_analysis' ? 'warning' : observation.status === 'corrective_action_requested' ? 'danger' : observation.status === 'action_completed' ? 'success' : observation.status === 'verified' ? 'primary' : 'dark'}>
                      {observation.status === 'submitted' ? 'Soumise' : observation.status === 'received' ? 'Reçue' : observation.status === 'in_analysis' ? 'En analyse' : observation.status === 'corrective_action_requested' ? 'Action requise' : observation.status === 'action_completed' ? 'Action réalisée' : observation.status === 'verified' ? 'Vérifiée' : 'Clôturée'}
                    </Badge>
                  )}
                </div>
              </BlockHeadContent>
              <BlockHeadContent>
                <div className="d-flex gap-2">
                  <Button
                    color="light"
                    outline
                    onClick={() => history.push("/observations")}
                  >
                    <Icon name="arrow-left"></Icon>
                    <span>Retour</span>
                  </Button>
                  <PermissionGate permission={["observations.edit", "observations.update", "Observations - Modifier"]}>
                    <Button
                      color="primary"
                      onClick={() => history.push(`/observations/${id}/edit`)}
                    >
                      <Icon name="edit"></Icon>
                      <span>Modifier</span>
                    </Button>
                  </PermissionGate>
                </div>
              </BlockHeadContent>
            </BlockBetween>
          </BlockHead>

          <Nav tabs className="nav-tabs-s2 mb-4">
            <NavItem>
              <NavLink
                href="#info"
                className={activeTab === "info" ? "active" : ""}
                onClick={(ev) => {
                  ev.preventDefault();
                  setActiveTab("info");
                }}
              >
                <Icon name="info" className="me-1" />
                Informations de l&apos;observation
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                href="#proofs"
                className={activeTab === "proofs" ? "active" : ""}
                onClick={(ev) => {
                  ev.preventDefault();
                  setActiveTab("proofs");
                }}
              >
                <Icon name="clip" className="me-1" />
                Preuves (fichiers / vidéos)
              </NavLink>
            </NavItem>
          </Nav>

          {activeTab === "info" ? (
          <div className="nk-block">
            <div className="row g-4">
              {/* Titre principal */}
              <div className="col-12">
                <div className="card border-0 bg-light">
                  <div className="card-body p-4">
                    <h4 className="mb-0">{observation.title || "Sans titre"}</h4>
                  </div>
                </div>
              </div>

              {/* Informations principales */}
              <div className="col-12">
                <h6 className="text-soft mb-3">
                  <Icon name="info" className="me-2"></Icon>
                  Informations principales
                </h6>
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="card border">
                      <div className="card-body p-3">
                        <div className="d-flex align-items-center mb-2">
                          <Icon name="map-pin" className="text-primary me-2"></Icon>
                          <span className="text-soft small">Site</span>
                        </div>
                        <div className="fw-bold">{observation.site?.name || "-"}</div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card border">
                      <div className="card-body p-3">
                        <div className="d-flex align-items-center mb-2">
                          <Icon name="tag" className="text-primary me-2"></Icon>
                          <span className="text-soft small">Type</span>
                        </div>
                        <div className="fw-bold">
                          {observation.type === 'positive' ? 'Observation positive' : 
                           observation.type === 'dangerous' ? 'Situation dangereuse' : 
                           observation.type === 'near_miss' ? "Presqu'accident" : 
                           observation.type === 'non_conformity' ? 'Non-conformité' : 
                           observation.type || "-"}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card border">
                      <div className="card-body p-3">
                        <div className="d-flex align-items-center mb-2">
                          <Icon name="alert-circle" className="text-warning me-2"></Icon>
                          <span className="text-soft small">Gravité</span>
                        </div>
                        <div className="fw-bold">
                          {observation.severity === 'low' ? 'Faible' : 
                           observation.severity === 'medium' ? 'Moyenne' : 
                           observation.severity === 'high' ? 'Élevée' : 
                           observation.severity || "-"}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card border">
                      <div className="card-body p-3">
                        <div className="d-flex align-items-center mb-2">
                          <Icon name="file-check" className="text-info me-2"></Icon>
                          <span className="text-soft small">Statut</span>
                        </div>
                        <div className="fw-bold">
                          {observation.status === 'submitted' ? 'Soumise' : 
                           observation.status === 'received' ? 'Reçue' : 
                           observation.status === 'in_analysis' ? 'En analyse' : 
                           observation.status === 'corrective_action_requested' ? 'Action requise' : 
                           observation.status === 'action_completed' ? 'Action réalisée' : 
                           observation.status === 'verified' ? 'Vérifiée' :
                           observation.status === 'closed' ? 'Clôturée' : 
                           observation.status || "-"}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card border">
                      <div className="card-body p-3">
                        <div className="d-flex align-items-center mb-2">
                          <Icon name="tag-alt" className="text-primary me-2"></Icon>
                          <span className="text-soft small">Catégorie</span>
                        </div>
                        <div className="fw-bold">
                          {observation.category === 'safety' ? 'Sécurité' : 
                           observation.category === 'environment' ? 'Environnement' : 
                           observation.category === 'quality' ? 'Qualité' : 
                           observation.category === 'health' ? 'Santé' : 
                           observation.category === 'other' ? 'Autre' : 
                           observation.category || "-"}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card border">
                      <div className="card-body p-3">
                        <div className="d-flex align-items-center mb-2">
                          <Icon name="calendar-alt" className="text-primary me-2"></Icon>
                          <span className="text-soft small">Date et heure de l'observation</span>
                        </div>
                        <div className="fw-bold">
                          {observation.date_observation ? new Date(observation.date_observation).toLocaleString("fr-FR", {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : "-"}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card border">
                      <div className="card-body p-3">
                        <div className="d-flex align-items-center mb-2">
                          <Icon name="user-check" className="text-success me-2"></Icon>
                          <span className="text-soft small">Assigné à</span>
                        </div>
                        <div className="fw-bold">
                          {observation.assignee ? `${observation.assignee.first_name || ''} ${observation.assignee.last_name || ''}`.trim() || observation.assignee.email : "-"}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card border">
                      <div className="card-body p-3">
                        <div className="d-flex align-items-center mb-2">
                          <Icon name="star" className="text-warning me-2"></Icon>
                          <span className="text-soft small">Score de qualité</span>
                        </div>
                        <div className="fw-bold fs-5">
                          {observation.quality_score != null && !isNaN(observation.quality_score)
                            ? parseFloat(observation.quality_score).toFixed(1)
                            : "0.0"}
                          <span className="text-soft small ms-1">/ 10</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="col-12">
                <h6 className="text-soft mb-3">
                  <Icon name="file-text" className="me-2"></Icon>
                  Description
                </h6>
                <div className="card border">
                  <div className="card-body p-4">
                    <p className="mb-0" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                      {observation.description || "Aucune description"}
                    </p>
                  </div>
                </div>
              </div>

              {(observation.source_inspection_id || observation.source_inspection?.id) && (
                <div className="col-12">
                  <div className="alert alert-pro alert-warning mb-0">
                    <h6 className="title mb-2">
                      <Icon name="notes-alt" className="me-2" />
                      Inspection source
                    </h6>
                    <a
                      className="link"
                      href={`${process.env.PUBLIC_URL || ""}/hse-process/inspections/${observation.source_inspection_id || observation.source_inspection?.id}`}
                    >
                      Inspection #{observation.source_inspection_id || observation.source_inspection?.id}
                      {observation.source_inspection?.title ? ` — ${observation.source_inspection.title}` : ""}
                    </a>
                  </div>
                </div>
              )}

              {((observation.derived_incidents && observation.derived_incidents.length > 0) ||
                (observation.derivedIncidents && observation.derivedIncidents.length > 0)) && (
                <div className="col-12">
                  <div className="alert alert-pro alert-danger mb-0">
                    <h6 className="title mb-2">
                      <Icon name="alert-circle" className="me-2" />
                      Incidents HSE liés
                    </h6>
                    <ul className="list list-sm mb-0">
                      {(observation.derived_incidents || observation.derivedIncidents || []).map((inc) => (
                        <li key={inc.id}>
                          <a
                            className="link"
                            href={`${process.env.PUBLIC_URL || ""}/hse-process/incidents?open_incident=${inc.id}`}
                          >
                            #{inc.id} — {inc.title}{" "}
                            <span className="text-soft">({inc.status})</span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Potentiel de risque */}
              {observation.risk_potential && (
                <div className="col-12">
                  <h6 className="text-soft mb-3">
                    <Icon name="alert-triangle" className="me-2"></Icon>
                    Potentiel de risque
                  </h6>
                  <div className="card border">
                    <div className="card-body p-4">
                      <p className="mb-0" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                        {observation.risk_potential}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Informations sur l'exposition */}
              {(observation.equipment_concerned || observation.people_exposed || observation.number_of_people_exposed != null || observation.exposure_duration || observation.immediate_action_taken) && (
                <div className="col-12">
                  <h6 className="text-soft mb-3">
                    <Icon name="users" className="me-2"></Icon>
                    Informations sur l'exposition
                  </h6>
                  <div className="card border">
                    <div className="card-body p-3">
                      <div className="row g-3">
                        {observation.equipment_concerned && (
                          <div className="col-md-6">
                            <span className="text-soft small d-block">Équipement concerné</span>
                            <span className="fw-bold">{observation.equipment_concerned}</span>
                          </div>
                        )}
                        {observation.people_exposed && (
                          <div className="col-md-6">
                            <span className="text-soft small d-block">Personnes exposées</span>
                            <span className="fw-bold">{observation.people_exposed}</span>
                          </div>
                        )}
                        {observation.number_of_people_exposed != null && (
                          <div className="col-md-6">
                            <span className="text-soft small d-block">Nombre de personnes exposées</span>
                            <span className="fw-bold">{observation.number_of_people_exposed}</span>
                          </div>
                        )}
                        {observation.exposure_duration && (
                          <div className="col-md-6">
                            <span className="text-soft small d-block">Durée d'exposition</span>
                            <span className="fw-bold">
                              {observation.exposure_duration === 'momentary' ? 'Momentanée' :
                               observation.exposure_duration === 'few_minutes' ? 'Quelques minutes' :
                               observation.exposure_duration === 'few_hours' ? 'Quelques heures' :
                               observation.exposure_duration === 'full_day' ? 'Journée complète' :
                               observation.exposure_duration === 'several_days' ? 'Plusieurs jours' :
                               observation.exposure_duration === 'ongoing' ? 'En cours' :
                               observation.exposure_duration}
                            </span>
                          </div>
                        )}
                        {observation.immediate_action_taken && (
                          <div className="col-12">
                            <Badge color="success">
                              <Icon name="check" className="me-1"></Icon>
                              Action immédiate prise
                            </Badge>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Localisation */}
              {(observation.location_description || observation.location_latitude || observation.location_longitude || observation.weather_conditions) && (
                <div className="col-12">
                  <h6 className="text-soft mb-3">
                    <Icon name="map" className="me-2"></Icon>
                    Localisation
                  </h6>
                  <div className="card border">
                    <div className="card-body p-3">
                      <div className="row g-3">
                        {observation.location_description && (
                          <div className="col-md-6">
                            <span className="text-soft small d-block">Description de la localisation</span>
                            <span className="fw-bold">{observation.location_description}</span>
                          </div>
                        )}
                        {(observation.location_latitude || observation.location_longitude) && (
                          <div className="col-md-6">
                            <span className="text-soft small d-block">Coordonnées GPS</span>
                            <span className="fw-bold">
                              {observation.location_latitude && observation.location_longitude
                                ? `${parseFloat(observation.location_latitude).toFixed(6)}, ${parseFloat(observation.location_longitude).toFixed(6)}`
                                : observation.location_latitude
                                  ? `Lat: ${parseFloat(observation.location_latitude).toFixed(6)}`
                                  : observation.location_longitude
                                    ? `Lng: ${parseFloat(observation.location_longitude).toFixed(6)}`
                                    : "-"}
                            </span>
                          </div>
                        )}
                        {observation.weather_conditions && (
                          <div className="col-md-6">
                            <span className="text-soft small d-block">Conditions météorologiques</span>
                            <span className="fw-bold">
                              {observation.weather_conditions === 'sunny' ? 'Ensoleillé' :
                               observation.weather_conditions === 'cloudy' ? 'Nuageux' :
                               observation.weather_conditions === 'rainy' ? 'Pluvieux' :
                               observation.weather_conditions === 'stormy' ? 'Orageux' :
                               observation.weather_conditions === 'windy' ? 'Venteux' :
                               observation.weather_conditions === 'foggy' ? 'Brumeux' :
                               observation.weather_conditions === 'dry' ? 'Sec' :
                               observation.weather_conditions === 'humid' ? 'Humide' :
                               observation.weather_conditions === 'other' ? 'Autre' :
                               observation.weather_conditions}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Proposition d'amélioration */}
              {observation.improvement_proposal && (
                <div className="col-12">
                  <h6 className="text-soft mb-3">
                    <Icon name="bulb" className="me-2"></Icon>
                    Proposition d'amélioration
                  </h6>
                  <div className="card border border-success">
                    <div className="card-body p-4">
                      <p className="mb-0" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                        {observation.improvement_proposal}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Informations de correction */}
              {(observation.correction_due_date || observation.estimated_cost != null) && (
                <div className="col-12">
                  <h6 className="text-soft mb-3">
                    <Icon name="calendar-check" className="me-2"></Icon>
                    Informations de correction
                  </h6>
                  <div className="card border">
                    <div className="card-body p-3">
                      <div className="row g-3">
                        {observation.correction_due_date && (
                          <div className="col-md-6">
                            <span className="text-soft small d-block">Date prévue de correction</span>
                            <span className="fw-bold">
                              {new Date(observation.correction_due_date).toLocaleDateString("fr-FR", {
                                day: '2-digit',
                                month: 'long',
                                year: 'numeric'
                              })}
                            </span>
                          </div>
                        )}
                        {observation.estimated_cost != null && (
                          <div className="col-md-6">
                            <span className="text-soft small d-block">Coût estimé</span>
                            <span className="fw-bold">{parseFloat(observation.estimated_cost).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} FCFA</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Informations complémentaires */}
              <div className="col-12">
                <h6 className="text-soft mb-3">
                  <Icon name="user" className="me-2"></Icon>
                  Informations complémentaires
                </h6>
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="card border">
                      <div className="card-body p-3">
                        <div className="d-flex align-items-center mb-2">
                          <Icon name="user-circle" className="text-primary me-2"></Icon>
                          <span className="text-soft small">Créée par</span>
                        </div>
                        <div className="fw-bold">
                          {observation.creator ? `${observation.creator.first_name || ''} ${observation.creator.last_name || ''}`.trim() || observation.creator.email : "-"}
                        </div>
                      </div>
                    </div>
                  </div>
                  {observation.assignee && (
                    <div className="col-md-6">
                      <div className="card border">
                        <div className="card-body p-3">
                          <div className="d-flex align-items-center mb-2">
                            <Icon name="user-check" className="text-success me-2"></Icon>
                            <span className="text-soft small">Assigné à</span>
                          </div>
                          <div className="fw-bold">
                            {observation.assignee ? `${observation.assignee.first_name || ''} ${observation.assignee.last_name || ''}`.trim() || observation.assignee.email : "-"}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="col-md-6">
                    <div className="card border">
                      <div className="card-body p-3">
                        <div className="d-flex align-items-center mb-2">
                          <Icon name="calendar" className="text-primary me-2"></Icon>
                          <span className="text-soft small">Date de création</span>
                        </div>
                        <div className="fw-bold">
                          {observation.created_at ? new Date(observation.created_at).toLocaleString("fr-FR", {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : "-"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          ) : (
            <div className="nk-block">
              <div className="card border">
                <div className="card-body p-4">
                  <h6 className="mb-3">
                    <Icon name="clip" className="me-2" />
                    Pièces justificatives de l&apos;observation
                  </h6>

                  {!Array.isArray(observation.attachments) || observation.attachments.length === 0 ? (
                    <div className="text-center py-4">
                      <Icon name="inbox" className="text-soft" style={{ fontSize: "2rem" }} />
                      <p className="text-soft mb-0 mt-2">Aucun fichier ou vidéo de preuve n&apos;est disponible.</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover align-middle mb-0">
                        <thead>
                          <tr>
                            <th>Nom du fichier</th>
                            <th>Type</th>
                            <th>Taille</th>
                            <th>Ajouté le</th>
                            <th className="text-end">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {observation.attachments.map((att) => {
                            const type = att.file_type || "";
                            const isImage = type.startsWith("image/");
                            const isVideo = type.startsWith("video/");

                            return (
                              <tr key={att.id}>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <Icon
                                      name={isImage ? "img" : isVideo ? "movie" : "file-doc"}
                                      className="me-2 text-primary"
                                    />
                                    <span className="fw-medium">{att.file_name || "Fichier"}</span>
                                  </div>
                                </td>
                                <td className="text-soft small">{type || "—"}</td>
                                <td>{formatBytes(att.file_size)}</td>
                                <td className="text-soft small">
                                  {att.created_at
                                    ? new Date(att.created_at).toLocaleString("fr-FR", {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })
                                    : "—"}
                                </td>
                                <td className="text-end">
                                  <Button
                                    type="button"
                                    onClick={() => openAttachment(att)}
                                    color="light"
                                    size="sm"
                                    className="me-1"
                                  >
                                    Ouvrir
                                  </Button>
                                  <Button
                                    type="button"
                                    onClick={() => downloadAttachment(att)}
                                    color="primary"
                                    size="sm"
                                  >
                                    Télécharger
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </Block>
      </Content>
    </React.Fragment>
  );
};

export default ObservationViewPage;

